import {
  type CatalogueItemResource,
  CatalogueItemsController_read_v1,
  type CataloguePriceResource,
  CataloguePricesController_list_v1,
  type UserResource,
} from '@repo/api-client';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconCircleCheck,
  IconLock,
  IconTag,
} from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { isForbidden, isNotFound } from '@/api/failure';
import { formatPaise } from '@/lib/money';

import { Pager } from '../back-office/Pager';
import { type PageState, ROWS_PER_PAGE } from '../back-office/pagination';
import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { paths } from '../paths';
import { useActorNames } from './actor-names';
import { isRetired } from './catalogue-kinds';
import { formatInstant } from './instant';
import { PriceAsOf } from './PriceAsOf';
import { RepriceDialog } from './RepriceDialog';

/**
 * Every price one catalogue item has ever carried, the price in force now, and
 * the way to set the next one.
 *
 * ## A ledger, not a table with a pencil in it
 *
 * **There is no edit and no delete, and that is enforced in the database.**
 * `catalogue_prices` refuses `UPDATE` and `DELETE` by trigger, so a row written
 * here is written for good — Reporting computes gross margin from these rows and
 * a sale records the exact `priceId` it charged at, which is only answerable
 * because nothing behind it moves. A mistake is corrected by repricing again,
 * and *both* entries stay visible, because the wrong price was genuinely in
 * force for however long it took somebody to notice.
 *
 * So the only interaction on this screen is **add the next price**. No row
 * carries a control, there is no undo, and there is deliberately nothing that
 * reads as "correct this entry". A pencil icon here would be an affordance for
 * something the storage layer refuses, which is the worst kind: it teaches staff
 * that history is editable and then fails at the last step.
 *
 * ## Owner-only at the route, not field by field
 *
 * Every row carries `costPaise`, and the API guards all three operations with
 * `@RequireRole('OWNER')` for that reason alone. The route declaration matches
 * (`route-table.tsx`), so a manager never opens this screen rather than opening
 * a version of it with a column missing — there is no such version, because the
 * reads themselves are refused. The 403 branch below is what a manager who
 * edited their role out of `sessionStorage` meets, and it is worded as a
 * boundary rather than as a fault.
 *
 * ## Paged, and the clearest case in the app for it
 *
 * `docs/ui-conventions.md`, "Reading a list", asks that the strategy be chosen
 * rather than defaulted. A price history grows every time somebody reprices and
 * never shrinks — it is the document's own example — so this pages through
 * `Pager` and never through `collectPages`. The **staff list** beside it is read
 * whole, from this same file, which is that rule applied to a different set
 * rather than an inconsistency: it is bounded by the park, it fills no filter
 * and no picker but resolves an id into a name on every row at once, and a
 * failure to read it costs a name rather than the screen.
 */

/** One read, in one of the three states it can be in. */
type Loaded<T> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly value: T }
  | { readonly kind: 'failed'; readonly error: unknown };

/** What the header reads: the item itself, and the price in force on it now. */
interface PriceHeader {
  readonly item: CatalogueItemResource;

  /**
   * The newest row, read as its own request rather than taken from the page
   * below.
   *
   * `history.items[0]` is the current price **only while the pager is on the
   * first page**, and the reprice form prefills the cost from it. Derived from
   * the page, a reprice started from page three would prefill a cost from
   * whenever that page happens to be and restate it as today's — a silent price
   * change nobody typed. One extra request buys an answer that does not depend
   * on where somebody happened to be reading.
   *
   * `null` is a real state for a moment: an item always has a price, because
   * creating one writes the first row, but a history read that answered an
   * empty page is not something to render `undefined` about.
   */
  readonly current: CataloguePriceResource | null;
}

/** Kept next to the header cells below, so the two cannot drift. */
const COLUMNS = 4;

export function PriceHistory() {
  const client = useApiClient();
  const { id: itemId } = useParams<{ id: string }>();

  const [offset, setOffset] = useState(0);

  const [header, setHeader] = useState<Loaded<PriceHeader>>({ kind: 'loading' });
  const [rows, setRows] = useState<Loaded<readonly CataloguePriceResource[]>>({ kind: 'loading' });

  // The window the last successful read answered with, kept outside the row
  // state on purpose: it survives the next load, so the pager and the count stay
  // where they are while a page is in flight instead of vanishing and coming
  // back under the pointer. `null` until the first answer.
  const [page, setPage] = useState<PageState | null>(null);

  const [repricing, setRepricing] = useState(false);

  // What just happened, said once above the ledger and left there. A toast
  // vanishes, and a price change is the kind of thing somebody wants to be able
  // to look back at after they have glanced away.
  const [notice, setNotice] = useState<string | null>(null);

  // A counter rather than a boolean, so two reloads in a row are two distinct
  // values and the second one still fires.
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  useEffect(() => {
    if (itemId === undefined) return;

    const controller = new AbortController();
    let live = true;

    setHeader({ kind: 'loading' });

    // Both halves of the header together, so the screen does not render an item
    // with a price that is still arriving, or offer to reprice before it knows
    // what the current cost is. `Promise.all` rejects on the first failure,
    // which is right: neither half is useful without the other.
    Promise.all([
      client.request(CatalogueItemsController_read_v1, {
        path: { id: itemId },
        signal: controller.signal,
      }),
      client.request(CataloguePricesController_list_v1, {
        path: { id: itemId },
        query: { offset: 0, limit: 1 },
        signal: controller.signal,
      }),
    ])
      .then(([item, newest]) => {
        if (live) setHeader({ kind: 'loaded', value: { item, current: newest.items[0] ?? null } });
      })
      .catch((error: unknown) => {
        if (live) setHeader({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, itemId, reloads]);

  useEffect(() => {
    if (itemId === undefined) return;

    // Two guards, not one. `controller` stops the request itself the moment the
    // page moves, and `live` stops an answer that was already in flight from
    // landing after this effect was torn down — without it, pressing Next twice
    // quickly can leave the second page's rows underneath the first's.
    const controller = new AbortController();
    let live = true;

    setRows({ kind: 'loading' });

    client
      .request(CataloguePricesController_list_v1, {
        path: { id: itemId },
        query: { offset, limit: ROWS_PER_PAGE },
        signal: controller.signal,
      })
      .then((answer) => {
        if (live) {
          setRows({ kind: 'loaded', value: answer.items });

          // The window the *server* answered with, not the one that was asked
          // for. They are the same today, and a pager that assumed so would be
          // silently wrong the day the API clamps a limit.
          setPage({ offset: answer.offset, limit: answer.limit, total: answer.total });
        }
      })
      .catch((error: unknown) => {
        if (live) setRows({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, itemId, offset, reloads]);

  const actors = useActorNames();

  // A route matched on `:id` always has one; this is the shape of somebody
  // reaching the URL some other way. A redirect rather than a not-found screen,
  // because the catalogue is where they were going.
  if (itemId === undefined) return <Navigate to={paths.catalogue} replace />;

  const item = header.kind === 'loaded' ? header.value.item : null;
  const current = header.kind === 'loaded' ? header.value.current : null;

  // Through `isRetired` rather than re-reading `retiredAt` here: the shipped
  // document renders that field's non-null branch as an empty object, so
  // "retired" is a presence check and not a date, and that reasoning lives in
  // one place (`catalogue-kinds.ts`) for both screens.
  const retired = item !== null && isRetired(item);

  // Either read refusing is the same refusal — the whole controller is
  // owner-only — so it is answered once, for the screen, rather than twice in
  // two different words.
  const refused =
    (header.kind === 'failed' && isForbidden(header.error)) ||
    (rows.kind === 'failed' && isForbidden(rows.error));

  const missing = header.kind === 'failed' && isNotFound(header.error);

  return (
    <Screen
      title="Price history"
      description="Every price this item has carried, newest first, with what it cost the garden at the time. Nothing here can be edited or removed — a correction is a new price, and both entries stay."
      action={
        // Hidden rather than shown and refused, per docs/ui-conventions.md. A
        // retired item cannot be repriced at all: the API answers
        // CATALOGUE_ITEM_RETIRED, a 409, and restoring it is a different
        // screen's job. Nothing to reprice while the header is still arriving
        // either, because the form prefills from it.
        item !== null && !retired && !refused ? (
          <Button
            onClick={() => {
              setRepricing(true);
            }}
          >
            <IconTag />
            Reprice
          </Button>
        ) : undefined
      }
      aside={
        <>
          {item !== null && (
            <ScreenFact label="Item">
              <span className="font-mono numeric">{item.code}</span> — {item.name}
              {retired && (
                <>
                  {' '}
                  {/* `outline` and not `destructive`: retiring an item is an
                      ordinary end to an ordinary life, and red is reserved for a
                      system that refused something or is about to. */}
                  <Badge variant="outline">Retired</Badge>
                </>
              )}
            </ScreenFact>
          )}

          {/* Said, rather than left as a missing button. The API refuses a
              reprice against a retired item with CATALOGUE_ITEM_RETIRED, and
              restoring it belongs to the catalogue screen — so this states the
              rule and where the way out is instead of showing a control that
              would 409. */}
          {retired && (
            <p className="text-sm text-muted-foreground">
              A retired item cannot be repriced. Its history is kept either way, and restoring it
              from the catalogue makes it sellable again at the price already in force.
            </p>
          )}

          {current !== null && (
            <ScreenFact label="In force now">
              <span className="font-mono numeric">{formatPaise(current.sellingPaise)}</span> selling
              <br />
              <span className="font-mono text-muted-foreground numeric">
                {formatPaise(current.costPaise)}
              </span>{' '}
              <span className="text-muted-foreground">cost</span>
            </ScreenFact>
          )}

          {page !== null && rows.kind !== 'failed' && (
            <ScreenFact label="Entries">
              <span className="font-mono numeric">{page.total}</span>{' '}
              {page.total === 1 ? 'price' : 'prices'}
            </ScreenFact>
          )}

          <Link
            to={paths.catalogue}
            className="inline-flex items-center gap-1.5 rounded-3xl text-sm text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30"
          >
            <IconArrowLeft className="size-4" aria-hidden />
            Back to the catalogue
          </Link>
        </>
      }
    >
      {refused ? (
        <OwnerOnly />
      ) : missing ? (
        <ItemMissing />
      ) : (
        <>
          {notice !== null && (
            <Alert
              // `status` and not the component's default `alert`: a price that
              // was accepted is not an interruption, and it is announced
              // politely rather than cutting across whatever is being read.
              role="status"
            >
              <IconCircleCheck />
              <AlertDescription>{notice}</AlertDescription>
            </Alert>
          )}

          {/* Above the ledger, because it is a question about the ledger: what
              was in force at a moment that is no longer the top row. */}
          <PriceAsOf itemId={itemId} actors={actors} />

          <ScreenTable label="Price history">
            <TableHeader>
              <TableRow>
                <TableHead className="w-52">In force from</TableHead>
                {/* Amounts right-align, per docs/ui-conventions.md — reading a
                    price history is the task of comparing down a column. */}
                <TableHead className="w-40 text-right">Selling</TableHead>
                <TableHead className="w-40 text-right">Cost</TableHead>
                <TableHead>Set by</TableHead>
              </TableRow>
            </TableHeader>

            {rows.kind === 'loading' && <TableSkeleton columns={COLUMNS} />}

            {rows.kind === 'failed' && (
              <TableNotice columns={COLUMNS}>
                <LoadFailed error={rows.error} onRetry={reload} />
              </TableNotice>
            )}

            {rows.kind === 'loaded' &&
              (rows.value.length === 0 ? (
                <TableNotice columns={COLUMNS}>
                  <EmptyState
                    title="No prices recorded"
                    description="Every item is created with a price, so an empty history means this one was read before its first row was written. Reload the screen; if it stays empty, report it."
                  />
                </TableNotice>
              ) : (
                <TableBody>
                  {rows.value.map((price) => (
                    <PriceRow
                      key={price.id}
                      price={price}
                      actor={actors.get(price.setByActorId)}
                      // Matched on the id rather than on "first row of the first
                      // page", so the badge is right on every page and stays
                      // right if the ordering ever changes.
                      inForce={price.id === current?.id}
                    />
                  ))}
                </TableBody>
              ))}
          </ScreenTable>

          {/* Not while the load failed: the notice above already says the rows
              could not be read, and a pager under it offers to move through rows
              that are not there. `Pager` draws nothing when everything fits on
              one page. */}
          {page !== null && rows.kind !== 'failed' && (
            <Pager state={page} label="prices" onOffsetChange={setOffset} />
          )}
        </>
      )}

      {/* Mounted only once there is a current price to restate, which is what
          the form prefills the unchanged amount from. */}
      {item !== null && !retired && (
        <RepriceDialog
          open={repricing}
          onOpenChange={setRepricing}
          item={item}
          current={current}
          onRepriced={(price) => {
            setRepricing(false);
            setNotice(
              `${item.name} now sells at ${formatPaise(price.sellingPaise)}, costing ${formatPaise(price.costPaise)}. The previous price stays in the history below.`,
            );

            // Back to the first page and re-read, rather than splicing the new
            // row in at the top. The server decides the order and the window,
            // and the newest row is on the first page by definition — a locally
            // patched list is a second answer to a question the server has
            // already answered.
            setOffset(0);
            reload();
          }}
        />
      )}
    </Screen>
  );
}

/**
 * One entry. Deliberately inert: no menu, no pencil, nothing that opens.
 *
 * The database refuses `UPDATE` and `DELETE` on this table by trigger, so there
 * is no control that could exist here even if a screen wanted one. See the note
 * at the top of this file before adding anything to this row.
 */
function PriceRow({
  price,
  actor,
  inForce,
}: {
  price: CataloguePriceResource;
  actor: UserResource | undefined;
  inForce: boolean;
}) {
  return (
    <TableRow>
      <TableCell className="whitespace-nowrap numeric">
        {formatInstant(price.effectiveFrom)}
        {inForce && (
          <>
            {' '}
            <Badge variant="outline">In force</Badge>
          </>
        )}
      </TableCell>

      <TableCell className="text-right font-mono text-foreground numeric">
        {formatPaise(price.sellingPaise)}
      </TableCell>

      {/* Quieter than the selling price and never coloured. A cost above the
          selling price is a loss leader and legitimate (FR-205), so flagging one
          would be this screen inventing a rule the API does not hold. */}
      <TableCell className="text-right font-mono numeric">{formatPaise(price.costPaise)}</TableCell>

      <TableCell>
        {actor === undefined ? (
          // Not a blank and not a guess. An actor with no matching person is
          // real — a staff list that failed to read, or an actor that is not a
          // user at all — and the id is what somebody quotes when they ask.
          <span className="font-mono text-xs text-muted-foreground numeric">
            {price.setByActorId}
          </span>
        ) : (
          <>
            <span className="text-foreground">{actor.displayName}</span>{' '}
            <span className="font-mono text-muted-foreground numeric">{actor.staffCode}</span>
          </>
        )}
      </TableCell>
    </TableRow>
  );
}

/**
 * A manager who reached this screen anyway, told where the boundary is.
 *
 * **Not `LoadFailed`.** "This list could not be loaded" is untrue: it was read,
 * and the answer was no. `RouteGuard`'s own `AccessRefused` sets the precedent
 * for wording an access refusal on screen rather than looking it up from the
 * code — the code's message is written for a control somebody pressed, and this
 * is a whole screen somebody opened.
 *
 * It names the reason and not the role, in the same spirit as the API's own
 * refusals: saying "an owner may" is the rule, saying "you are a manager" is
 * describing the permission model back to whoever is probing it. No retry —
 * `AUTH_FORBIDDEN` is non-retryable in the API's own registry, and a Try again
 * button under it is a control that cannot work.
 */
function OwnerOnly() {
  return (
    <Alert>
      <IconLock />
      <AlertTitle>Price history is the owner&rsquo;s alone</AlertTitle>
      <AlertDescription>
        Every entry states what the garden paid for the item as well as what it sold for, so only an
        owner can open this. The catalogue itself, and the price each item sells at, are readable
        without it.
      </AlertDescription>
    </Alert>
  );
}

/** The item behind the URL is not there — a stale link, or one typed by hand. */
function ItemMissing() {
  return (
    <Alert>
      <IconAlertTriangle />
      <AlertTitle>There is no such item</AlertTitle>
      <AlertDescription>
        Nothing in the catalogue has this address. It may have been opened from a link that is out
        of date. Go back to the catalogue and pick the item from the list.
      </AlertDescription>
    </Alert>
  );
}
